/**
 * Feedback Form Module
 * Handles user interactions with the feedback form block
 */

/**
 * Get reCAPTCHA token
 * @param {string} siteKey - The reCAPTCHA site key
 * @returns {Promise<string>} - The reCAPTCHA token
 */
const getReCaptchaToken = (siteKey) => {
    return new Promise((resolve, reject) => {
        // In development the reCAPTCHA script isn't loaded; submit with an empty
        // token so the backend dev bypass accepts it (matches membershipForm.js).
        if (typeof grecaptcha === 'undefined') {
            resolve('');
            return;
        }
        grecaptcha.ready(() => {
            grecaptcha.execute(siteKey, { action: 'submit' })
                .then(token => resolve(token))
                .catch(error => reject(error));
        });
    });
};

const initFeedbackForm = () => {
    const forms = document.querySelectorAll('[data-feedback-form]');

    forms.forEach(form => {
        const thanksView = form.querySelector('[data-feedback-thanks]');
        const negativeView = form.querySelector('[data-feedback-negative]');
        const heading = form.querySelector('.feedback-form-heading');
        const subheading = form.querySelector('.feedback-form-subheading');
        const buttonsContainer = form.querySelector('.feedback-form-buttons');
        const yesButton = form.querySelector('[data-feedback-yes]');
        const noButton = form.querySelector('[data-feedback-no]');
        const feedbackForm = form.querySelector('[data-feedback-form-submit]');
        const reasonInputs = form.querySelectorAll('[data-feedback-reason]');
        const textareaContainer = form.querySelector('[data-feedback-textarea]');
        const requiresTextInput = form.querySelector('[data-feedback-reason-requires-text]');
        const errorContainer = form.querySelector('[data-feedback-error]');

        // Handle "Ja" button click
        if (yesButton) {
            yesButton.addEventListener('click', async () => {
                // Add active state to Yes button, inactive to No button
                yesButton.classList.add('active');
                yesButton.classList.remove('inactive');
                noButton.classList.remove('active');
                noButton.classList.add('inactive');

                // Get antiforgery token
                const antiForgeryToken = feedbackForm.querySelector('[name="__RequestVerificationToken"]').value;

                // Get reCAPTCHA token
                const submitButton = feedbackForm.querySelector('[data-feedback-submit-recaptcha]');
                const siteKey = submitButton.getAttribute('data-sitekey');
                let token = '';

                try {
                    token = await getReCaptchaToken(siteKey);
                } catch (error) {
                    console.error('reCAPTCHA error:', error);
                    alert('Ett fel uppstod med säkerhetsverifieringen. Försök igen senare.');
                    return;
                }

                // Submit positive feedback
                const pageUrl = feedbackForm.querySelector('[name="PageUrl"]').value;
                const contentId = feedbackForm.querySelector('[name="ContentId"]').value;

                await submitFeedback({
                    pageUrl,
                    contentId,
                    foundWhat: true,
                    reason: null,
                    freeText: null,
                    token
                }, antiForgeryToken);

                // Show thank you message (hide all other content)
                heading.style.display = 'none';
                if (subheading) subheading.style.display = 'none';
                buttonsContainer.style.display = 'none';
                negativeView.style.display = 'none';
                thanksView.style.display = 'block';
            });
        }

        // Handle "Nej" button click
        if (noButton) {
            noButton.addEventListener('click', () => {
                // Add active state to No button, inactive to Yes button
                noButton.classList.add('active');
                noButton.classList.remove('inactive');
                yesButton.classList.remove('active');
                yesButton.classList.add('inactive');

                // Show heading/buttons and negative form
                heading.style.display = '';
                if (subheading) subheading.style.display = '';
                buttonsContainer.style.display = '';
                thanksView.style.display = 'none';
                negativeView.style.display = 'block';
            });
        }

        // Handle reason selection
        reasonInputs.forEach(input => {
            input.addEventListener('change', () => {
                if (input === requiresTextInput && input.checked) {
                    textareaContainer.style.display = 'block';
                } else if (input.checked && input !== requiresTextInput) {
                    textareaContainer.style.display = 'none';
                }
            });
        });

        // Handle form submission
        if (feedbackForm) {
            feedbackForm.addEventListener('submit', async (e) => {
                e.preventDefault();

                // Validate that a reason is selected
                const selectedReason = form.querySelector('[data-feedback-reason]:checked');
                if (!selectedReason) {
                    errorContainer.style.display = 'block';
                    return;
                }

                errorContainer.style.display = 'none';

                // Get antiforgery token
                const antiForgeryToken = feedbackForm.querySelector('[name="__RequestVerificationToken"]').value;

                // Get reCAPTCHA token
                const submitButton = feedbackForm.querySelector('[data-feedback-submit-recaptcha]');
                const siteKey = submitButton.getAttribute('data-sitekey');
                let token = '';

                try {
                    token = await getReCaptchaToken(siteKey);
                } catch (error) {
                    console.error('reCAPTCHA error:', error);
                    alert('Ett fel uppstod med säkerhetsverifieringen. Försök igen senare.');
                    return;
                }

                // Collect form data
                const formData = new FormData(feedbackForm);
                const data = {
                    pageUrl: formData.get('PageUrl'),
                    contentId: formData.get('ContentId') ? parseInt(formData.get('ContentId')) : null,
                    foundWhat: formData.get('FoundWhat') === 'true',
                    reason: formData.get('Reason'),
                    freeText: formData.get('FreeText') || null,
                    token
                };

                // Submit feedback
                const success = await submitFeedback(data, antiForgeryToken);

                if (success) {
                    // Show thank you message (hide all other content)
                    heading.style.display = 'none';
                    if (subheading) subheading.style.display = 'none';
                    buttonsContainer.style.display = 'none';
                    negativeView.style.display = 'none';
                    thanksView.style.display = 'block';
                }
            });
        }
    });
};

/**
 * Submit feedback to the server
 * @param {Object} data - Feedback data
 * @param {string} antiForgeryToken - The antiforgery token
 * @returns {Promise<boolean>} - Success status
 */
const submitFeedback = async (data, antiForgeryToken) => {
    try {
        // Include antiforgery token in the body
        data.__RequestVerificationToken = antiForgeryToken;

        const response = await fetch('/feedback/submit', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            },
            body: new URLSearchParams(data)
        });

        if (!response.ok) {
            console.error('Feedback submission failed with status:', response.status);
            alert('Ett fel uppstod när feedbacken skulle skickas. Försök igen senare.');
            return false;
        }

        const result = await response.json();

        if (!result.success) {
            console.error('Feedback submission failed:', result.errorMessage);
            alert('Ett fel uppstod när feedbacken skulle skickas. Försök igen senare.');
            return false;
        }

        return true;
    } catch (error) {
        console.error('Error submitting feedback:', error);
        alert('Ett fel uppstod när feedbacken skulle skickas. Försök igen senare.');
        return false;
    }
};

// Initialize on DOM ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFeedbackForm);
} else {
    initFeedbackForm();
}

export { initFeedbackForm };
